import {
  BusinessUnitModel,
  CompanyDepartmentModel,
  CompanyModel,
  DefaultScheduleDto,
  GdprExportStatus,
  UpdateDefaultScheduleRequest,
  UserModel,
  ZipCodeLocalAreaModel,
} from 'api/model';
import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from 'components/Modal';
import useUserForm, {
  cleanWeekSchedule,
  parseHomeAnchorIds,
  partTimePercentageOptions,
  tieredCapabilities,
  UserFormField,
  userFormToUserModel,
} from './useUserForm';
import CapabilityLevelTable from './CapabilityLevelTable';
import styled from 'styled-components';
import { useApiCallFn, useResponseFn } from 'hooks/useApiCallFn';
import Select from 'components/inputs/Select';
import LabelWrap from 'components/inputs/LabelWrap';
import Input from 'components/inputs/Input';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import useTranslations from 'contexts/basicData/useTranslations';
import Checkbox from 'components/inputs/Checkbox';
import MediaQuery from 'constants/MediaQuery';
import WeeklySchedule from 'pages/Employees/WeeklySchedule';
import {
  convertToDateOfBirthString,
  isValidDateString,
} from 'utils/date-helpers';
import filterableUserFields, {
  userPropsArray,
} from 'pages/Employees/filterableUserFields';
import { arraySpreadIf } from 'utils/spreading';
import TextArea from 'components/inputs/TextArea';
import useBusinessUnits from 'contexts/basicData/useBusinessUnits';
import useCompanies from 'contexts/basicData/useCompanies';
import isHomeAnchorCandidate from './homeAnchorCandidate';
import { SelectOption } from 'components/inputs/SearchableSelect';
import Button from 'components/inputs/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCalendarAlt,
  faCaretDown,
  faCaretRight,
  faFileArchive,
  faFlask,
  faSpinner,
  faTrashAlt,
  faTrashRestoreAlt,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import {
  getEmptyDefaultScheduleDto,
  ScheduleErrors,
  validateSchedule,
} from 'components/Availability/helpers';
import TextButton from 'components/inputs/TextButton';
import SectionHeader from 'components/SectionHeader';
import useModalStack from 'contexts/modal/useModalStack';
import { useTeamsGetTeams } from 'api/teams/teams';
import useMe from 'contexts/authentication/useMe';
import UserChangeLogSection from './UserChangeLogSection';
import Roles, { includesRoles } from 'constants/Roles';
import UserPaymentCardForm from './UserPaymentCardForm';
import UserChargingKeyForm from './UserChargingKeyForm';
import expenseUtils from 'components/DetailedCase/Expenses/expenseUtils';
import { useCompanyDepartmentGetCompanyDepartments } from 'api/company-department/company-department';
import UserRatingStatsWidget from './UserRatingStatsWidget';
import { useSupplierGetSuppliers } from 'api/supplier/supplier';
import {
  useUserCreateUser,
  useUserDeleteEmployeeScheduleHook,
  useUserGetDefaultEmployeeScheduleHook,
  useUserUpdateUserAndSchedule,
} from 'api/user/user';
import { useZipCodeLocalAreaFindZipCodeLocalAreaByZipCodeHook } from 'api/zip-code-local-area/zip-code-local-area';
import { showException } from 'utils/exception-helper';
import {
  useExportDownloadGdprExportHook,
  useExportGetGdprExportStatus,
  useExportInitiateGdprExport,
} from 'api/export/export';
import { downloadBlob } from 'utils/file-utils';

const GlobalFormWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  grid-gap: 20px;
  padding: 20px;
  ${MediaQuery.mobileL} {
    grid-template-columns: 1fr;
  }
  ${MediaQuery.laptop} {
    grid-template-columns: 300px 1fr;
  }

  ${MediaQuery.laptopL} {
    grid-template-columns: 300px 250px 1fr;
  }
`;

const MyTextButton = styled(TextButton)<{
  disabled?: boolean;
  inverted?: boolean;
}>`
  color: ${({ theme, disabled, inverted }) =>
    inverted
      ? theme.colors.foreground.primary
      : disabled
        ? theme.colors.foreground.tertiary
        : theme.colors.foreground.button};
  margin-left: auto;
  padding: 0px 10px 0px 0px;
`;

const SubFormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
`;

const FormWrap = styled.div<{ maxWidth?: number; compact?: boolean }>`
  padding-right: 10px;
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => (compact ? '0' : '10px')};
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth}px;` : '')}
`;

const MyLabelWrap = styled(LabelWrap)<{ width?: number; disabled?: boolean }>`
  & > *:last-child {
    width: ${({ width }) => (width ? `${width}px` : 'auto')};
  }
  ${({ disabled }) => disabled && 'opacity: 0.5;'}
`;

const UserProp = styled.div`
  display: grid;
  grid-template-columns: 22px auto;
`;

const HorizontalWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

/** Upplysning under hemankarväljaren. Amber, inte rött: fältet är tomt, inte felaktigt ifyllt. */
const HomeAnchorHint = styled.div`
  margin-top: 4px;
  font-size: 0.85em;
  color: rgb(138, 98, 6);
`;

/** Förklaringen till hur listan används — neutral, till skillnad från varningen ovan. */
const HomeAnchorHelp = styled.div`
  margin-top: 4px;
  font-size: 0.85em;
  opacity: 0.75;
`;

const HomeAnchorList = styled.ol`
  margin: 0 0 6px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HomeAnchorRow = styled.li`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HomeAnchorName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Knapparna på en hemankarrad. Inte MyTextButton: den är gjord för den orange schemarubriken och
 * har vit text, så på det vita formuläret syntes bara de inaktiverade (grå) pilarna.
 */
const HomeAnchorButton = styled(TextButton)`
  padding: 0 4px;
  color: ${({ theme, disabled }) =>
    disabled
      ? theme.colors.background.tertiary
      : theme.colors.background.button};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

const Paragraph = styled.p`
  padding-bottom: 3px;
`;

const StyledTextArea = styled(TextArea)`
  height: 100px;
`;
interface Props {
  user?: UserModel;
  onCancel?(): void;
  onSaved?(): void;
  canEdit?: boolean;
  title?: string;
}

const ScheduleHeader = styled.div<{ deleted?: boolean }>`
  display: flex;
  /* color: white;
  background-color: rgb(204, 102, 51); */
  background-color: ${({ theme, deleted }) =>
    !deleted
      ? theme.colors.foreground.secondary
      : theme.colors.background.tertiary};
  color: ${({ theme }) => theme.colors.background.primary};
  font-weight: 500;
  border-radius: 3px;
  user-select: none;
  line-height: ${({ theme }) => theme.sizes.font.large};
  padding-left: 20px;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  cursor: pointer;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const DefaultScheduleSection = styled.div`
  display: flex;
  width: 500px;
  flex-direction: column;
`;

const StatsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export interface DeletableDefaultScheduleDto extends DefaultScheduleDto {
  deleted?: boolean;
}

/**
 * Hemankaret är FRIVILLIGT och blockerar inte sparningen, varken här eller i backend. Men
 * ruttplaneringen räknar dagen från och till den adressen och läser inte bostadsadressen, så en
 * fälttestare utan hemankare utesluts ur den automatiska planeringen. Det är värt att säga direkt
 * i formuläret i stället för att låta det upptäckas i planeringsvyn. Samma budskap som varningen
 * i personallistan (EmployeeOverview).
 */
const homeAnchorMissingMessage =
  'Utan hemankare ingår fälttestaren inte i ruttplaneringen — hemankaret är den adress dagen planeras att börja och sluta vid.';

/**
 * Betald resa till hemankaret (TL-feedback 2026-10-13). Bara för fälttestare där Zäkra betalar
 * resan, t.ex. från Borås till Göteborg C — annars är resan till hemankaret egen pendling.
 */
const paidTravelHelpMessage =
  'Kryssa i när Zäkra betalar resan mellan bostadsorten och hemankaret. Ruttplaneringen räknar då resan som en kostnad och föredrar förare som bor närmare. Sätts av HR.';

/** Varför listan har en ordning, och varför den sällan avgör (flera-hemankare-plan.md B3). */
const homeAnchorHelpMessage =
  'Planeringen utgår från det ankare som ligger närmast dagens första respektive sista uppdrag. Ordningen avgör bara när två ligger nästan lika nära.';

const UserFormModal: FC<Props> = ({
  user,
  onCancel,
  onSaved,
  canEdit,
  title,
}) => {
  const modalStack = useModalStack();
  const { units } = useBusinessUnits();
  const { companies, companiesById } = useCompanies();
  const { data: teams = [] } = useTeamsGetTeams();
  const me = useMe();

  const [defaultWeekSchedule, setDefaultWeekSchedule] = useState<
    DeletableDefaultScheduleDto[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedScheduleIndex, setSelectedScheduleIndex] = useState<
    number | null
  >(0);
  const [scheduleErrors, setScheduleErrors] = useState<ScheduleErrors[]>([]);
  const [zipCodeLocalArea, setZipCodeLocalArea] = useState<
    ZipCodeLocalAreaModel | undefined
  >(user?.zipCodeLocalArea);
  const { rolesById, employmentStatuses } = useTranslations();
  const { input, select, checkbox, onChange, setMultiple, state, validation } =
    useUserForm(user);
  const currentState = useMemo(() => userFormToUserModel(state), [state]);
  const createMode = !user;

  // HR har samma rättigheter som SuperAdmin i personalkortet, utom att dela ut rollen
  // SuperAdmin. Speglar PersonnelCardAdminRoles i UserLogic — servern är den som verkligen spärrar.
  const isPersonnelCardAdmin = includesRoles(
    [Roles.SuperAdmin, Roles.HR],
    me?.roles
  );
  const canAssignSuperAdmin = includesRoles([Roles.SuperAdmin], me?.roles);

  // Att stänga av någon för bokning stoppar all inbokning och överskuggar hela schemat.
  // Speglar rollistan i UserLogic.UpdateUser — servern är den som verkligen spärrar.
  const canChangeAvailableForDriving = includesRoles(
    [
      Roles.SuperAdmin,
      Roles.HR,
      Roles.KvalitetOUtbildning,
      Roles.TrafikLedarAdmin,
    ],
    me?.roles
  );

  // Historiken visar personuppgifter och anledningar till avstängning för bokning.
  // Speglar behörighetsspärren på UserController.GetUserChangeLogs — utan rollen
  // får anropet 403, så sektionen ska inte ens ritas ut.
  const canViewChangeLog = includesRoles(
    [Roles.SuperAdmin, Roles.HR],
    me?.roles
  );

  // Bostadsadressen är HR:s ansvar. Speglar spärren
  // i UserLogic.UpdateUser; när ett nytt kort skapas finns ingen adress att skydda.
  const canEditHomeAddress =
    createMode || includesRoles([Roles.SuperAdmin, Roles.HR], me?.roles);

  // Betald resa till hemankaret är en lönefråga och sätts av HR. Speglar spärren i
  // UserLogic.UpdateUser — servern är den som verkligen spärrar.
  const canEditPaidTravel = includesRoles(
    [Roles.SuperAdmin, Roles.HR],
    me?.roles
  );

  // Rutan har ett eget läge: ibockad men ännu utan plats. Annars hade en ibockning behövt välja
  // en plats åt HR, och en missad väljare gav betald resa från fel ort (granskning 2026-10-13).
  const [paidTravelBoxChecked, setPaidTravelBoxChecked] = useState(false);
  const paidTravelChecked =
    paidTravelBoxChecked || state[UserFormField.paidTravelFromCompanyID] !== '';

  // Blockera sparning när anledningen saknas, så att serverns 400 aldrig behöver visas.
  const missingUnavailableReason =
    state[UserFormField.isAvailableForDriving] !== 'true' &&
    state[UserFormField.unavailableForDrivingReason].trim() === '';

  const findZipCodeLocalAreaByZipCode =
    useZipCodeLocalAreaFindZipCodeLocalAreaByZipCodeHook();
  const getZipCodeAreaCall = useApiCallFn((zipCode: string) =>
    findZipCodeLocalAreaByZipCode({ zipCode })
  );

  const getCompanyDepartmentsCall = useCompanyDepartmentGetCompanyDepartments();
  const getSuppliersCall = useSupplierGetSuppliers();

  // disable isAllowedToChangeAvailability if all schedules are deleted
  useEffect(() => {
    if (defaultWeekSchedule.every((d) => d.deleted)) {
      onChange(UserFormField.isAllowedToChangeAvailability, 'false');
    } else {
      onChange(UserFormField.isAllowedToChangeAvailability, 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultWeekSchedule]);

  useEffect(() => {
    const handleChangedZipCode = async (newCode: string) => {
      const [result] = await getZipCodeAreaCall.run(newCode);
      onChange(UserFormField.zipCodeLocalAreaID, result?.id?.toString() ?? '');
      setZipCodeLocalArea(result ?? undefined);
    };

    if (currentState.postalCode.length > 0) {
      handleChangedZipCode(currentState.postalCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState.postalCode]);

  const createUserCallMutation = useUserCreateUser();
  const updateUserAndScheduleCallMutation = useUserUpdateUserAndSchedule();

  // GDPR Export - async flow
  const [gdprExportRequestId, setGdprExportRequestId] = useState<number | null>(
    null
  );
  const [gdprExportPassword, setGdprExportPassword] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const initiateGdprExportMutation = useExportInitiateGdprExport();
  const downloadGdprExport = useExportDownloadGdprExportHook();

  // Poll for export status when we have a requestId
  const gdprExportStatusQuery = useExportGetGdprExportStatus(
    { requestId: gdprExportRequestId ?? 0 },
    {
      query: {
        enabled: gdprExportRequestId !== null,
        refetchInterval: (data) => {
          // Stop polling when completed, failed, or expired
          if (
            data?.status === GdprExportStatus.Completed ||
            data?.status === GdprExportStatus.Failed ||
            data?.status === GdprExportStatus.Expired
          ) {
            return false;
          }
          return 3000; // Poll every 3 seconds while pending/processing
        },
      },
    }
  );

  const handleInitiateGdprExport = async () => {
    if (!user?.userID) {
      showException('Kunde inte starta GDPR-export: användare saknas');
      return;
    }
    try {
      const response = await initiateGdprExportMutation.mutateAsync({
        data: {
          userId: user.userID,
          password: gdprExportPassword || undefined,
        },
      });
      if (response?.requestId) {
        setGdprExportRequestId(response.requestId);
      } else {
        showException('Oväntat svar från servern vid GDPR-export');
      }
    } catch (error) {
      showException(error);
    }
  };

  const handleDownloadGdprExport = async (partNumber?: number) => {
    if (!gdprExportRequestId) return;
    setIsDownloading(true);
    try {
      const blob = await downloadGdprExport({
        requestId: gdprExportRequestId,
        partNumber: partNumber ?? 1,
      });
      if (blob) {
        const safeName = (user?.name ?? String(user?.userID)).replace(
          /[^a-zA-Z0-9åäöÅÄÖ]/g,
          '_'
        );
        const today = new Date().toISOString().split('T')[0];
        const partSuffix =
          partNumber && (gdprExportStatusQuery.data?.fileCount ?? 1) > 1
            ? `_del${partNumber}`
            : '';
        downloadBlob(blob, `GDPR_Export_${safeName}_${today}${partSuffix}.zip`);
      }
    } catch (error) {
      showException(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const resetGdprExport = () => {
    setGdprExportRequestId(null);
    setGdprExportPassword('');
  };

  const deleteEmployeeSchedule = useUserDeleteEmployeeScheduleHook();
  const getDefaultEmployeeSchedule = useUserGetDefaultEmployeeScheduleHook();

  const deleteScheduleApiCall = useApiCallFn(
    (userId: number, scheduleId: number) =>
      deleteEmployeeSchedule({ userId, scheduleId })
  );

  const defaultWeekScheduleResponse = useResponseFn(async () => {
    if (user) {
      const response = await getDefaultEmployeeSchedule({
        userId: user.userID,
      });
      if (response.length > 0) {
        const errors = validateSchedule(response);

        if (errors?.some((e) => e.errors.length > 0)) {
          setScheduleErrors(errors);
        }
        setDefaultWeekSchedule(response);
      }
    }
  }, [currentState.userID]);

  // Hemankaret är den adress fälttestaren startar och slutar dagen vid. Ruttplaneringen läser
  // inte bostadsadressen, så en fälttestare utan hemankare ingår inte i den automatiska
  // planeringen — därav upplysningen nedan. Se ruttplanering-att-gora-plan.md § 1.3.
  const isFieldTester = useMemo(
    () =>
      state[UserFormField.roles]
        .split(',')
        .filter((v) => v)
        .some((roleId) => rolesById[Number(roleId)] === 'Fälttestare'),
    [state, rolesById]
  );

  const homeAnchorOptions = useMemo((): SelectOption<
    number | null,
    CompanyModel | null
  >[] => {
    // Bara företag med koordinat kan användas för planering; ett hemankare utan koordinat är
    // lika oanvändbart som inget hemankare, så det ska inte gå att välja.
    // Lös olikhet mot null är avsiktlig: API:t skickar null för saknad koordinat, inte undefined,
    // så !== undefined släppte igenom 204 av 268 aktiva företag (upptäckt i QA-körningen).
    const selectable = companies.filter(
      (c) => c.latitude != null && c.longitude != null
    );
    const byName = (a: CompanyModel, b: CompanyModel) =>
      a.name.localeCompare(b.name, 'sv');
    const sorted = [
      // Ankarkandidaterna (se homeAnchorCandidate.ts) sorteras först i listan.
      ...selectable.filter(isHomeAnchorCandidate).sort(byName),
      ...selectable.filter((c) => !isHomeAnchorCandidate(c)).sort(byName),
    ];

    // Orten utelämnas när den saknas, annars blir etiketten "Namn —" med hängande tankstreck.
    const toLabel = (c: CompanyModel) =>
      c.city ? `${c.name} — ${c.city}` : c.name;

    const options: SelectOption<number | null, CompanyModel | null>[] =
      sorted.map((company) => ({
        label: toLabel(company),
        value: company.companyID,
        extra: company,
      }));
    return options;
  }, [companies]);

  const selectedAnchorIds = useMemo(
    () => parseHomeAnchorIds(state[UserFormField.homeAnchorCompanyIDs]),
    [state]
  );

  // Etiketten för ett valt ankare. Ett redan satt ankare ska alltid synas som det är, även om
  // företaget hunnit bli inaktivt eller tappat sin koordinat (`companies` innehåller bara aktiva
  // och väljaren bara koordinatsatta) — annars ser personalkortet tomt ut fast det bär en adress.
  // Planeringen hoppar över ett sådant ankare, så det märks.
  const anchorLabel = (companyId: number) => {
    const option = homeAnchorOptions.find((o) => o.value === companyId);
    if (option) {
      return option.label;
    }
    const company = companiesById.get(companyId);
    return company
      ? `${company.city ? `${company.name} — ${company.city}` : company.name} (kan inte planeras)`
      : `Okänt hemankare (ID ${companyId})`;
  };

  const setAnchorIds = (ids: number[]) => {
    onChange(UserFormField.homeAnchorCompanyIDs, ids.join(','));
  };

  const moveAnchor = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= selectedAnchorIds.length) {
      return;
    }
    const next = [...selectedAnchorIds];
    [next[index], next[target]] = [next[target], next[index]];
    setAnchorIds(next);
  };

  // Styr bara upplysningstexten under väljaren — sparningen går igenom ändå.
  const homeAnchorMissing = isFieldTester && selectedAnchorIds.length === 0;

  // Föreslå närmaste centralstation som hemankare när fältet är tomt. En nyanställd har ingen
  // historik att luta sig mot, och ett tomt fält tar fälttestaren ur ruttplaneringen — så det
  // billigaste är att fylla i en rimlig gissning som går att ändra.
  //
  // Bara när fältet är TOMT: en satt adress är någons val, och den skriver vi aldrig över. Hittas
  // ingen station lämnas fältet tomt — vi faller aldrig tillbaka på en hubb, förslaget ska vara
  // en station eller ingenting.
  //
  // Närhet mäts på antalet GEMENSAMMA INLEDANDE SIFFROR i postnummret, inte på differensen.
  // Differens är fel mått: Stockholm C ligger på 111 20, så en fälttestare i Bromma (168 xx) får
  // |16800 − 11120| = 5680 mot Malmö C:s |16800 − 21120| = 4320 och hamnar i Skåne. Engångskörningen
  // (DiagnosticsConsole --assign-station-anchors) gjorde precis det felet och gav Malmö 236
  // fälttestare mot Stockholms 111. Inledande siffror är däremot geografiska: "1" delas med
  // Stockholm men inte med Malmö.
  useEffect(() => {
    if (!isFieldTester || state[UserFormField.homeAnchorCompanyIDs] !== '') {
      return;
    }

    const ownZip = currentState.postalCode.replace(/\D/g, '');
    if (ownZip.length < 2) {
      return;
    }

    const sharedPrefixLength = (zip: string) => {
      let shared = 0;
      while (
        shared < ownZip.length &&
        shared < zip.length &&
        ownZip[shared] === zip[shared]
      ) {
        shared += 1;
      }
      return shared;
    };

    const nearestStation = companies
      .filter(
        (c) =>
          c.isCentralStation === true &&
          c.active &&
          c.latitude != null &&
          c.longitude != null
      )
      .map((c) => {
        const stationZip = c.zip.replace(/\D/g, '');
        return {
          company: c,
          shared: sharedPrefixLength(stationZip),
          // Tie-break inom samma region, där differensen faktiskt betyder något.
          difference: Math.abs(Number(ownZip) - Number(stationZip)),
        };
      })
      .filter((candidate) => candidate.shared > 0)
      .sort(
        (a, b) => b.shared - a.shared || a.difference - b.difference
      )[0]?.company;

    if (nearestStation) {
      onChange(
        UserFormField.homeAnchorCompanyIDs,
        nearestStation.companyID.toString()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState.postalCode, isFieldTester, companies]);

  const businessUnitOptions = useMemo((): SelectOption<
    number | null,
    BusinessUnitModel | null
  >[] => {
    const options: SelectOption<number | null, BusinessUnitModel | null>[] =
      units.map((unit) => ({
        label: unit.name,
        value: unit.id,
        extra: unit,
      }));
    options.unshift({
      label: 'Välj enhet',
      value: null,
      extra: null,
    });
    return options;
  }, [units]);

  const companyDepartmentOptions = useMemo((): SelectOption<
    number | null,
    CompanyDepartmentModel | null
  >[] => {
    const options: SelectOption<
      number | null,
      CompanyDepartmentModel | null
    >[] =
      getCompanyDepartmentsCall.data?.map((unit) => ({
        label: unit.name,
        value: unit.id,
        extra: unit,
      })) ?? [];
    options.unshift({
      label: 'Välj avdelning',
      value: null,
      extra: null,
    });
    return options;
  }, [getCompanyDepartmentsCall.data]);

  const roleOptions = useMemo(
    (): Option<string>[] =>
      Object.entries(rolesById).map(([roleId, roleName]) => ({
        value: roleId,
        label: roleName,
        disabled: Number(roleId) === Roles.SuperAdmin && !canAssignSuperAdmin,
      })),
    [rolesById, canAssignSuperAdmin]
  );

  const supplierOptions = useMemo(
    (): Option<string>[] =>
      getSuppliersCall.data
        ?.sort((supplierA, supplierB) =>
          supplierA.isActive === supplierB.isActive
            ? 0
            : supplierA.isActive
              ? -1
              : 1
        )
        .map((supplier) => ({
          label: supplier.isActive
            ? supplier.name
            : `${supplier.name} (inaktiv)`,
          value: supplier.id.toString(),
        })) ?? [],
    [getSuppliersCall.data]
  );

  const employmentStatusOptions = useMemo(
    (): Option<string>[] =>
      Object.entries(employmentStatuses).map(([status, name]) => {
        const option = {
          label: name as string,
          value: status,
        };
        return option;
      }),

    [employmentStatuses]
  );

  const speedCheckOptions = useMemo(() => {
    return Array.from({ length: 11 }, (_, index) => index).map((i) => {
      let label = `${i * 10} % av körningarna`;
      switch (i) {
        case 0:
          label = 'Aldrig';
          break;
        case 10:
          label = 'Alltid';
          break;
        default:
          break;
      }
      return (
        <option key={`${i / 10}`} value={i / 10}>
          {label}
        </option>
      );
    });
  }, []);

  const teamOptions = useMemo(() => {
    const options = teams.map((team) => {
      return (
        <option key={team.id} value={team.id}>
          {team.name}
        </option>
      );
    });
    options.unshift(
      <option key="välj team-option" value={undefined}>
        Välj team
      </option>
    );
    return options;
  }, [teams]);

  const mkInput = (
    name: UserFormField,
    label: string,
    placeholder?: string,
    width?: number,
    disabled?: boolean
  ) => (
    <MyLabelWrap label={label} width={width}>
      <Input
        disabled={!canEdit || disabled}
        onChange={input(name)}
        placeholder={placeholder}
        value={state[name]}
      />
    </MyLabelWrap>
  );

  const mkTextArea = (
    name: UserFormField,
    label: string,
    placeholder?: string,
    width?: number,
    errorLabel?: string
  ) => (
    <MyLabelWrap errorLabel={errorLabel} label={label} width={width}>
      <StyledTextArea
        disabled={!canEdit}
        onChange={(v) => onChange(name, v.target.value)}
        placeholder={placeholder}
        value={state[name]}
      />
    </MyLabelWrap>
  );

  const mkDateInput = (
    name: UserFormField,
    label: string,
    placeholder?: string
  ) => {
    const value = convertToDateOfBirthString(state[name]);
    return (
      <MyLabelWrap label={label}>
        <Input
          disabled={!canEdit}
          onChange={input(name)}
          placeholder={placeholder}
          value={value}
          warning={!isValidDateString(value)}
        />
      </MyLabelWrap>
    );
  };

  const mkCheckbox = (
    name: UserFormField,
    label: ReactNode,
    disabled?: boolean
  ) => (
    <MyLabelWrap key={name} disabled={disabled}>
      <Checkbox
        checked={state[name] === 'true'}
        disabled={!canEdit || disabled}
        onChange={checkbox(name)}
      >
        {label}
      </Checkbox>
    </MyLabelWrap>
  );

  const handleCreateClick = async () => {
    const userModel = userFormToUserModel(state);

    await createUserCallMutation.mutateAsync(
      {
        data: userModel,
      },
      {
        onSuccess: () => {
          onSaved?.();
        },
      }
    );
  };

  const deleteSchedule = async (s: DefaultScheduleDto): Promise<boolean> => {
    if (!user || s.id === undefined) return false;

    const [, deleteError] = await deleteScheduleApiCall.run(user.userID, s.id);
    if (deleteError) {
      return false;
    }
    return true;
  };

  const handleUpdateClick = async () => {
    if (!user) return;

    // validate schedule
    const errors = validateSchedule(defaultWeekSchedule);
    if (errors?.some((e) => e.errors.length > 0)) {
      setScheduleErrors(errors);
      return;
    }

    // delete any schedules that were removed
    const deletedWeekSchedules = defaultWeekSchedule.filter((s) => s.deleted);
    const undeletedWeekSchedules = defaultWeekSchedule.filter(
      (s) => !s.deleted
    );
    if (deletedWeekSchedules.length > 0) {
      const deleteResult = await Promise.all(
        deletedWeekSchedules.map(async (s) => deleteSchedule(s))
      );
      if (deleteResult.some((r) => !r)) {
        // something went wrong, abort mission
        return;
      }
    }

    // update the user and schedule
    try {
      await updateUserAndScheduleCallMutation.mutateAsync({
        data: {
          user: userFormToUserModel(state, user),
          schedules: cleanWeekSchedule(undeletedWeekSchedules),
        } satisfies UpdateDefaultScheduleRequest,
      });
      defaultWeekScheduleResponse.update();
      onSaved?.();
    } catch (updateUserError) {
      showException(updateUserError);
    }
  };

  const handleCancelClick = () => {
    // return any deleted schedules to the active list
    setDefaultWeekSchedule((prev) => [
      ...prev.map(
        (s) =>
          ({
            ...s,
            deleted: false,
          }) as DeletableDefaultScheduleDto
      ),
    ]);
    onCancel?.();
  };

  const toggleDeleteSchedule = async (
    scheduleIndex: number,
    deleted: boolean
  ) => {
    if (!user?.userID) throw new Error('Unable to find userID');
    setDefaultWeekSchedule((prev) => [
      ...prev.map(
        (s, i) =>
          ({
            ...s,
            deleted: i === scheduleIndex ? deleted : s.deleted, // toggle the deleted flag for the schedule index
          }) as DeletableDefaultScheduleDto
      ),
    ]);
  };

  const handleSetWeek = useCallback(
    (newWeek: DefaultScheduleDto, index: number) => {
      const scheduleToValidate = defaultWeekSchedule.reduce((acc, cur, i) => {
        if (i === index) {
          return [...acc, newWeek];
        }

        return [...acc, cur];
      }, [] as DefaultScheduleDto[]);

      if (scheduleErrors?.some((e) => e.errors.length > 0)) {
        const errors = validateSchedule(scheduleToValidate);

        setScheduleErrors(errors ?? []);
      }
      setDefaultWeekSchedule(scheduleToValidate);
    },
    [defaultWeekSchedule, scheduleErrors]
  );

  useEffect(() => {
    if (defaultWeekSchedule.length !== 0) {
      setSelectedScheduleIndex(defaultWeekSchedule.length - 1); // select the last schedule
    }
  }, [defaultWeekSchedule.length]);

  const saveStatus = createMode
    ? createUserCallMutation.status
    : updateUserAndScheduleCallMutation.status;
  const renderContent = () => {
    switch (saveStatus) {
      case 'idle':
      case 'success':
      case 'error':
        return (
          <>
            {user?.roles.includes(Roles.SuperAdmin) && (
              <FormWrap>
                <StatsSection>
                  <UserRatingStatsWidget user={user} />
                </StatsSection>
              </FormWrap>
            )}
            <GlobalFormWrap>
              <FormWrap maxWidth={270}>
                {mkInput(UserFormField.name, 'Namn')}
                {mkInput(UserFormField.employmentID, 'Anställnings-ID')}
                {mkInput(UserFormField.greenPlate, 'Grönskylt')}
                {mkDateInput(
                  UserFormField.dateOfBirth,
                  'Födelsedatum',
                  'ÅÅÅÅ-MM-DD'
                )}
                {mkInput(UserFormField.email, 'E-post')}
                {mkInput(UserFormField.phoneNumber, 'Telefon')}
                <HorizontalWrap>
                  {mkInput(
                    UserFormField.postalCode,
                    'Postnr',
                    undefined,
                    76,
                    !canEditHomeAddress
                  )}
                  {mkInput(
                    UserFormField.city,
                    'Ort',
                    undefined,
                    undefined,
                    !canEditHomeAddress
                  )}
                </HorizontalWrap>
                <HorizontalWrap>
                  Område: {zipCodeLocalArea?.name ?? ''}
                </HorizontalWrap>
                <MyLabelWrap label="Hemankare">
                  {selectedAnchorIds.length > 0 && (
                    <HomeAnchorList>
                      {selectedAnchorIds.map((companyId, index) => (
                        <HomeAnchorRow key={companyId}>
                          <HomeAnchorName title={anchorLabel(companyId)}>
                            {index + 1}. {anchorLabel(companyId)}
                          </HomeAnchorName>
                          <HomeAnchorButton
                            aria-label="Flytta upp"
                            disabled={!canEdit || index === 0}
                            onClick={() => moveAnchor(index, -1)}
                            title="Flytta upp"
                            type="button"
                          >
                            <FontAwesomeIcon icon={faArrowUp} />
                          </HomeAnchorButton>
                          <HomeAnchorButton
                            aria-label="Flytta ned"
                            disabled={
                              !canEdit || index === selectedAnchorIds.length - 1
                            }
                            onClick={() => moveAnchor(index, 1)}
                            title="Flytta ned"
                            type="button"
                          >
                            <FontAwesomeIcon icon={faArrowDown} />
                          </HomeAnchorButton>
                          <HomeAnchorButton
                            aria-label="Ta bort"
                            disabled={!canEdit}
                            onClick={() =>
                              setAnchorIds(
                                selectedAnchorIds.filter(
                                  (id) => id !== companyId
                                )
                              )
                            }
                            title="Ta bort"
                            type="button"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </HomeAnchorButton>
                        </HomeAnchorRow>
                      ))}
                    </HomeAnchorList>
                  )}
                  <Select
                    disabled={!canEdit}
                    onChange={(event) => {
                      const companyId = Number(event.currentTarget.value);
                      if (companyId && !selectedAnchorIds.includes(companyId)) {
                        setAnchorIds([...selectedAnchorIds, companyId]);
                      }
                    }}
                    value=""
                  >
                    <option value="">
                      {selectedAnchorIds.length === 0
                        ? 'Välj hemankare'
                        : 'Lägg till hemankare'}
                    </option>
                    {homeAnchorOptions
                      .filter(
                        (option) =>
                          option.value != null &&
                          !selectedAnchorIds.includes(option.value)
                      )
                      .map((option) => (
                        <option key={option.value} value={option.value ?? ''}>
                          {option.label}
                        </option>
                      ))}
                  </Select>
                  {homeAnchorMissing && (
                    <HomeAnchorHint>{homeAnchorMissingMessage}</HomeAnchorHint>
                  )}
                  {selectedAnchorIds.length > 1 && (
                    <HomeAnchorHelp>{homeAnchorHelpMessage}</HomeAnchorHelp>
                  )}
                  {isFieldTester && (
                    <>
                      <label htmlFor="paid-travel-to-anchor">
                        <input
                          checked={paidTravelChecked}
                          id="paid-travel-to-anchor"
                          disabled={!canEdit || !canEditPaidTravel}
                          onChange={(event) => {
                            const { checked } = event.currentTarget;
                            setPaidTravelBoxChecked(checked);
                            if (!checked) {
                              onChange(
                                UserFormField.paidTravelFromCompanyID,
                                ''
                              );
                            }
                          }}
                          type="checkbox"
                        />{' '}
                        Betald resa till hemankaret
                      </label>
                      {paidTravelChecked && (
                        <Select
                          disabled={!canEdit || !canEditPaidTravel}
                          onChange={(event) =>
                            onChange(
                              UserFormField.paidTravelFromCompanyID,
                              event.currentTarget.value
                            )
                          }
                          title="Platsen resan betalas från, t.ex. stationen i orten där fälttestaren bor"
                          value={state[UserFormField.paidTravelFromCompanyID]}
                        >
                          <option value="">
                            Välj plats resan betalas från
                          </option>
                          {homeAnchorOptions
                            .filter((option) => option.value != null)
                            .map((option) => (
                              <option
                                key={option.value}
                                value={option.value ?? ''}
                              >
                                Från {option.label}
                              </option>
                            ))}
                        </Select>
                      )}
                      {paidTravelChecked &&
                        state[UserFormField.paidTravelFromCompanyID] === '' && (
                          <HomeAnchorHint>
                            Välj platsen — utan plats sparas ingen betald resa.
                          </HomeAnchorHint>
                        )}
                      <HomeAnchorHelp>{paidTravelHelpMessage}</HomeAnchorHelp>
                    </>
                  )}
                </MyLabelWrap>
                <MyLabelWrap label="Enhet">
                  <Select
                    disabled={!canEdit}
                    onChange={(event) => {
                      onChange(
                        UserFormField.businessUnitID,
                        event.currentTarget.value
                      );
                    }}
                    value={state[UserFormField.businessUnitID]}
                  >
                    {businessUnitOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value ?? undefined}
                      >
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </MyLabelWrap>
                <MyLabelWrap label="Avdelning">
                  <Select
                    disabled={!canEdit}
                    onChange={(event) => {
                      onChange(
                        UserFormField.companyDepartmentID,
                        event.currentTarget.value
                      );
                    }}
                    value={state[UserFormField.companyDepartmentID]}
                  >
                    {companyDepartmentOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value ?? undefined}
                      >
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </MyLabelWrap>
                <MyLabelWrap label="Roller">
                  <MultiSelect
                    disabled={!canEdit}
                    onChange={(v) =>
                      onChange(UserFormField.roles, Array.from(v).join(','))
                    }
                    options={roleOptions}
                    value={
                      new Set(
                        state[UserFormField.roles].split(',').filter((v) => v)
                      )
                    }
                  />
                </MyLabelWrap>
                <MyLabelWrap
                  info={{
                    title: 'Signatur',
                    content: (
                      <div>
                        <Paragraph>
                          Avgör vad som visas som avsändare (namn eller endast
                          en roll) på meddelanden och kommentarer som
                          fälttestare ser i appen.
                        </Paragraph>
                      </div>
                    ),
                  }}
                  label="Signatur "
                >
                  <Select
                    disabled={!isPersonnelCardAdmin || !canEdit}
                    onChange={(event) => {
                      onChange(
                        UserFormField.commentSignature,
                        event.currentTarget.value
                      );
                    }}
                    value={state[UserFormField.commentSignature]}
                  >
                    <option value="">
                      {currentState.name || 'Användarens namn'}
                    </option>
                    {state[UserFormField.roles]
                      .split(',')
                      .filter((v) => v && rolesById[v])
                      .map((roleId) => (
                        <option key={roleId} value={rolesById[roleId]}>
                          {rolesById[roleId]}
                        </option>
                      ))}
                  </Select>
                </MyLabelWrap>
                <MyLabelWrap label="Hanterar leverantörer">
                  <MultiSelect
                    disabled={!canEdit}
                    onChange={(v) =>
                      onChange(
                        UserFormField.supplierIds,
                        Array.from(v).join(',')
                      )
                    }
                    options={supplierOptions}
                    value={
                      new Set(
                        state[UserFormField.supplierIds]
                          .split(',')
                          .filter((v) => v)
                      )
                    }
                  />
                </MyLabelWrap>
                <MyLabelWrap label="Anställningsstatus">
                  <Select
                    disabled={!canEdit}
                    onChange={(event) => {
                      onChange(
                        UserFormField.employmentStatusID,
                        event.currentTarget.value
                      );
                    }}
                    value={state[UserFormField.employmentStatusID]}
                  >
                    {employmentStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </MyLabelWrap>
                {/* Heltidsprocent och Deltid, procent delar plats: heltidare ser den första
                    (bara HR och SuperAdmin), behovsanställda den andra. */}
                {state[UserFormField.isFullTime] === 'true' &&
                  includesRoles([Roles.HR, Roles.SuperAdmin], me?.roles) && (
                  <MyLabelWrap
                    errorLabel={
                      validation[UserFormField.fullTimePercentage]?.warning
                    }
                    info={{
                      title: 'Heltidsprocent',
                      content: (
                        <div>
                          <Paragraph>
                            Ange procent för heltidsanställda (0-100%).
                          </Paragraph>
                          <Paragraph>
                            Detta fält är endast synligt för HR och super-admin.
                          </Paragraph>
                          <Paragraph>
                            <strong>
                              OBS: Detta fält måste fyllas i manuellt av HR.
                            </strong>
                          </Paragraph>
                        </div>
                      ),
                    }}
                    label="Heltidsprocent (%) "
                  >
                    <Input
                      disabled={!canEdit}
                      max={100}
                      min={0}
                      onChange={({ currentTarget: { value } }) => {
                        if (
                          value === '' ||
                          (/^\d+$/.test(value) &&
                            Number(value) >= 0 &&
                            Number(value) <= 100)
                        ) {
                          onChange(UserFormField.fullTimePercentage, value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent decimal point, comma, and other non-digit characters
                        if (
                          e.key === '.' ||
                          e.key === ',' ||
                          e.key === 'e' ||
                          e.key === 'E' ||
                          e.key === '-' ||
                          e.key === '+'
                        ) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="0-100"
                      step={1}
                      type="number"
                      value={state[UserFormField.fullTimePercentage]}
                    />
                  </MyLabelWrap>
                )}
                {state[UserFormField.isFullTime] !== 'true' && (
                  <MyLabelWrap
                    info={{
                      title: 'Deltid, procent',
                      content: (
                        <Paragraph>
                          Visas i fälttestarväljaren. Påverkar ingen
                          beräkning.
                        </Paragraph>
                      ),
                    }}
                    label="Deltid, procent"
                  >
                    <Select
                      disabled={!canEdit}
                      onChange={select(UserFormField.partTimePercentage)}
                      value={state[UserFormField.partTimePercentage]}
                    >
                      <option value="">Ej angiven</option>
                      {partTimePercentageOptions.map((percentage) => (
                        <option key={percentage} value={String(percentage)}>
                          {`${percentage} %`}
                        </option>
                      ))}
                    </Select>
                  </MyLabelWrap>
                )}
                <MyLabelWrap label="Grupp ">
                  <Select
                    disabled={!canEdit}
                    onChange={select(UserFormField.teamID)}
                    value={state[UserFormField.teamID]}
                  >
                    {teamOptions}
                  </Select>
                </MyLabelWrap>
                <MyLabelWrap
                  info={{
                    title: 'Automatisk fortkörningsavstämning',
                    content: (
                      <div>
                        <Paragraph>
                          Hur ofta (i snitt) som en förares körningar
                          automatiskt kollas för fortkörningar.
                        </Paragraph>
                        <Paragraph>
                          Det går annars att göra detta manuellt från
                          uppdragsvyn när uppdraget är slutfört.
                        </Paragraph>
                        <Paragraph>
                          <strong>
                            Obs! Inställningen sätts automatiskt till 20% efter
                            10 fläckfria körningar
                          </strong>
                        </Paragraph>
                      </div>
                    ),
                  }}
                  label="Automatisk fortkörningsavstämning "
                >
                  <Select
                    disabled={!canEdit}
                    onChange={select(UserFormField.autoFetchSpeedLimitRate)}
                    value={state[UserFormField.autoFetchSpeedLimitRate]}
                  >
                    {speedCheckOptions}
                  </Select>
                </MyLabelWrap>
                {mkCheckbox(
                  UserFormField.excludeFromInvoicing,
                  'Exkludera från fakturaunderlag'
                )}
                {mkCheckbox(
                  UserFormField.excludeFromSalaryReport,
                  'Exkludera från lönerapport'
                )}
              </FormWrap>
              <FormWrap compact>
                {mkCheckbox(UserFormField.isActive, 'Aktiv')}
                {mkCheckbox(
                  UserFormField.isAvailableForDriving,
                  'Tillgänglig för bokning',
                  !canChangeAvailableForDriving
                )}
                {state[UserFormField.isAvailableForDriving] !== 'true' && (
                  <>
                    {mkTextArea(
                      UserFormField.unavailableForDrivingReason,
                      'Anledning till att medarbetaren inte är bokningsbar *',
                      'T.ex. indraget körkort, sjukskrivning, pågående utredning',
                      undefined,
                      validation[UserFormField.unavailableForDrivingReason]
                        ?.error
                    )}
                    {mkDateInput(
                      UserFormField.unavailableForDrivingUntil,
                      'Återställs automatiskt den',
                      'ÅÅÅÅ-MM-DD'
                    )}
                  </>
                )}
                {mkCheckbox(
                  UserFormField.autoApproveAssignment,
                  'Uppdrag kan godkännas automatiskt om de uppfyller kriterierna'
                )}
                {mkCheckbox(
                  UserFormField.showInInvoicing,
                  'Visa i fakturaunderlag'
                )}
                {mkCheckbox(
                  UserFormField.isTruckTransport,
                  'Extern lastbilstransportör'
                )}
                <CapabilityLevelTable
                  disabled={!canEdit}
                  setMultiple={setMultiple}
                  state={state}
                />
                {userPropsArray
                  .filter(
                    (key) => !tieredCapabilities.some((t) => t.can === key)
                  )
                  .map((key) => {
                    // filterable user fields
                    const field = Number(
                      key
                    ) as keyof typeof filterableUserFields;

                    return mkCheckbox(
                      field,
                      <UserProp>
                        {filterableUserFields[field].icon(true)}
                        {filterableUserFields[field].title}
                      </UserProp>
                    );
                  })}
                {isPersonnelCardAdmin &&
                  mkCheckbox(
                    UserFormField.isBetaTester,
                    <UserProp>
                      <FontAwesomeIcon icon={faFlask} /> Beta-testare
                    </UserProp>
                  )}
                {mkCheckbox(
                  UserFormField.shouldSeeTimeReportSectionInApp,
                  <UserProp>
                    <FontAwesomeIcon icon={faCalendarAlt} /> Visa
                    tidrapportering i appen
                  </UserProp>
                )}
                {mkCheckbox(
                  UserFormField.isAllowedToOverrideAssigneeWarnings,
                  <UserProp>
                    <FontAwesomeIcon icon={faWarning} /> Får överrida
                    FT-varningar vid tilldelning
                  </UserProp>,
                  !isPersonnelCardAdmin
                )}
                {mkCheckbox(
                  UserFormField.isAllowedHalfdayPlanning,
                  <UserProp>
                    <FontAwesomeIcon icon={faWarning} /> Tillåt
                    halvdagsplanering
                  </UserProp>,
                  !isPersonnelCardAdmin
                )}
              </FormWrap>

              <DefaultScheduleSection>
                <SectionHeader>Tillgänglighet</SectionHeader>
                <>
                  {/* {mkCheckbox(
                    UserFormField.isAllowedToChangeAvailability,
                    <Paragraph>
                      Kan ändra tillgänglighet själv i appen
                    </Paragraph>,
                    defaultWeekSchedule.every((d) => d.deleted)
                    dölj tills vidare
                  )} */}
                  {defaultWeekSchedule.map((week, index) => (
                    <Fragment key={index}>
                      <ScheduleHeader
                        deleted={week.deleted}
                        onClick={() => {
                          setSelectedScheduleIndex((prev) =>
                            prev === index ? null : index
                          );
                        }}
                      >
                        <FontAwesomeIcon
                          icon={
                            index === selectedScheduleIndex
                              ? faCaretDown
                              : faCaretRight
                          }
                        />
                        <span>{index === 0 ? 'Basschema' : 'Extraschema'}</span>
                        {week.deleted ? (
                          <MyTextButton
                            disabled={!canEdit}
                            inverted
                            onClick={() => {
                              if (
                                !defaultWeekSchedule[index].isBaseSchedule &&
                                defaultWeekSchedule.filter(
                                  (s) => s.isBaseSchedule && s.deleted
                                ).length === 1
                              ) {
                                // if this is an extra schedule and the base schedule is also, restore both schedules
                                defaultWeekSchedule.forEach((s, i) => {
                                  toggleDeleteSchedule(i, false);
                                });
                              } else {
                                // otherwise, restore only this one
                                toggleDeleteSchedule(index, false);
                              }
                            }}
                            title="Återställ"
                          >
                            Återställ{' '}
                            <FontAwesomeIcon icon={faTrashRestoreAlt} />
                          </MyTextButton>
                        ) : (
                          <MyTextButton
                            disabled={!canEdit}
                            onClick={() => {
                              if (
                                defaultWeekSchedule.filter((s) => !s.deleted)
                                  .length > 1 &&
                                index === 0
                              ) {
                                modalStack.push(
                                  <Modal
                                    buttons={[
                                      {
                                        label: 'Ok',
                                        onClick: () => {
                                          modalStack.pop();
                                        },
                                      },
                                    ]}
                                  >
                                    Du måste ta bort det andra schemat innan du
                                    kan ta bort basschemat.
                                  </Modal>
                                );
                              } else {
                                toggleDeleteSchedule(index, true);
                              }
                            }}
                            title="Ta bort"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </MyTextButton>
                        )}
                      </ScheduleHeader>
                      {defaultWeekSchedule.length === 0 ||
                        (selectedScheduleIndex === index && (
                          <WeeklySchedule
                            canEdit={canEdit}
                            errors={scheduleErrors[index]}
                            index={index}
                            key={week.id}
                            setWeek={(newWeek: DefaultScheduleDto) =>
                              handleSetWeek(newWeek, index)
                            }
                            week={week}
                          />
                        ))}
                    </Fragment>
                  ))}

                  <ButtonRow>
                    {defaultWeekSchedule.length < 2 && (
                      <Button
                        onClick={() => {
                          // if there is a base schedule that is not deleted, add the new schedule as an extra schedule (isBaseSchedule = false)
                          // if there is a deleted base schedule, prompt the user to restore it first
                          // if there is no base schedule, create a new base schedule (isBaseSchedule = true)
                          const baseSchedule = defaultWeekSchedule.find(
                            (s) => s.isBaseSchedule && !s.deleted
                          );
                          if (baseSchedule) {
                            setDefaultWeekSchedule((prev) => [
                              ...prev,
                              getEmptyDefaultScheduleDto(false),
                            ]);
                          } else if (
                            defaultWeekSchedule.find(
                              (s) => s.isBaseSchedule && s.deleted
                            )
                          ) {
                            modalStack.push(
                              <Modal
                                buttons={[
                                  {
                                    label: 'Ok',
                                    onClick: () => {
                                      modalStack.pop();
                                    },
                                  },
                                ]}
                              >
                                Du måste återställa det befintliga basschemat
                                innan du kan lägga till ett nytt.
                              </Modal>
                            );
                          } else {
                            setDefaultWeekSchedule((prev) => [
                              ...prev,
                              getEmptyDefaultScheduleDto(true),
                            ]);
                          }
                        }}
                      >
                        {`+ Lägg till ${
                          defaultWeekSchedule.filter((s) => s.isBaseSchedule)
                            .length > 0
                            ? 'extra'
                            : 'bas'
                        }schema`}
                      </Button>
                    )}
                  </ButtonRow>
                </>
              </DefaultScheduleSection>
            </GlobalFormWrap>
            {expenseUtils.allowCircleKSensitiveInfo(me) && user && (
              <SubFormWrap>
                <UserPaymentCardForm user={user} />
                <UserChargingKeyForm user={user} />
              </SubFormWrap>
            )}
            <SubFormWrap>
              <FormWrap>
                {mkTextArea(UserFormField.otherInfo, 'Anteckningar')}
              </FormWrap>
            </SubFormWrap>
            {user && canViewChangeLog && (
              <SubFormWrap>
                <UserChangeLogSection userId={user.userID} />
              </SubFormWrap>
            )}
            {isPersonnelCardAdmin && user && (
              <SubFormWrap>
                <SectionHeader>GDPR</SectionHeader>
                {!gdprExportRequestId ? (
                  <div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <span
                        style={{ display: 'block', marginBottom: '0.25rem' }}
                      >
                        Lösenord för ZIP-fil (valfritt):
                      </span>
                      <input
                        type="password"
                        value={gdprExportPassword}
                        onChange={(e) => setGdprExportPassword(e.target.value)}
                        placeholder="Lämna tomt för okrypterad fil"
                        aria-label="Lösenord för ZIP-fil"
                        style={{
                          display: 'block',
                          width: '100%',
                          maxWidth: '300px',
                          padding: '0.5rem',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                        disabled={initiateGdprExportMutation.isLoading}
                      />
                      {gdprExportPassword && (
                        <p
                          style={{
                            fontSize: '0.85rem',
                            color: '#666',
                            marginTop: '0.25rem',
                          }}
                        >
                          Filen kommer krypteras med AES-256
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleInitiateGdprExport}
                      disabled={initiateGdprExportMutation.isLoading}
                    >
                      {initiateGdprExportMutation.isLoading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin /> Startar
                          export...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faFileArchive} /> Exportera
                          användardata (GDPR)
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p style={{ marginBottom: '0.5rem' }}>
                      <strong>Status:</strong>{' '}
                      {gdprExportStatusQuery.data?.statusText ?? 'Laddar...'}
                      {(gdprExportStatusQuery.data?.status ===
                        GdprExportStatus.Pending ||
                        gdprExportStatusQuery.data?.status ===
                          GdprExportStatus.Processing) && (
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          style={{ marginLeft: '0.5rem' }}
                        />
                      )}
                    </p>
                    {gdprExportStatusQuery.data?.status ===
                      GdprExportStatus.Completed &&
                      ((gdprExportStatusQuery.data?.fileCount ?? 1) > 1 ? (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            marginBottom: '0.5rem',
                          }}
                        >
                          {Array.from(
                            {
                              length:
                                gdprExportStatusQuery.data?.fileCount ?? 1,
                            },
                            (_, i) => i + 1
                          ).map((partNumber) => (
                            <Button
                              key={partNumber}
                              onClick={() =>
                                handleDownloadGdprExport(partNumber)
                              }
                              disabled={isDownloading}
                            >
                              {isDownloading ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                              ) : (
                                <FontAwesomeIcon icon={faFileArchive} />
                              )}{' '}
                              Del {partNumber}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleDownloadGdprExport()}
                          disabled={isDownloading}
                          style={{ marginRight: '0.5rem' }}
                        >
                          {isDownloading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin /> Laddar
                              ner...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faFileArchive} /> Ladda ner
                              export
                            </>
                          )}
                        </Button>
                      ))}
                    {gdprExportStatusQuery.data?.status ===
                      GdprExportStatus.Failed && (
                      <p style={{ color: 'red', marginBottom: '0.5rem' }}>
                        Fel: {gdprExportStatusQuery.data?.errorMessage}
                      </p>
                    )}
                    <TextButton onClick={resetGdprExport}>
                      Starta ny export
                    </TextButton>
                  </div>
                )}
              </SubFormWrap>
            )}
          </>
        );

      case 'loading':
      default:
        return <LoadingSpinner>Sparar...</LoadingSpinner>;
    }
  };

  return (
    <Modal
      buttons={
        saveStatus === 'loading'
          ? []
          : [
              ...arraySpreadIf(!!canEdit, {
                label: createMode ? 'Skapa användare' : 'Uppdatera användare',
                onClick: createMode ? handleCreateClick : handleUpdateClick,
                disabled: missingUnavailableReason,
              }),
              {
                label: canEdit ? 'Avbryt' : 'Stäng',
                onClick: handleCancelClick,
              },
            ]
      }
      onClose={handleCancelClick}
      title={
        title !== undefined
          ? title
          : createMode
            ? 'Skapa användare'
            : 'Redigera användare'
      }
    >
      {renderContent()}
    </Modal>
  );
};

export default UserFormModal;
